import { useState } from 'react';

import { FlexAligner, TabBase, Tabs, Typography } from '@scaleaq/scaleui';
import { InventoryTree } from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { responsive } from '@/utils';

import { OrbitOneGalleryContext } from './orbitOneImageGalleryModal.types.ts';
import { OrbitOneImageGalleryPanel } from './orbitOneImageGalleryModalContent/OrbitOneImageGalleryPanel.tsx';
import {
  welfareFilterClasses,
  liceFilterClasses,
  liceGalleryFilterClasses,
} from './orbitOneImageGalleryModalContent/orbitOneImageGalleryPanel/fishObservationGallery/fishObservationGallery.constants.ts';
import {
  OrbitOneGalleryFilterClass,
  OrbitOneGalleryFilterState,
} from './orbitOneImageGalleryModalContent/orbitOneImageGalleryPanel/fishObservationGallery/fishObservationGallery.types.ts';

interface OrbitOneImageGalleryModalContentProps {
  /** OrbitOne camera(s) backing the configured sensor. */
  cameras: InventoryTree[];
  galleryContext: OrbitOneGalleryContext;
}

/**
 * Image gallery modal body. With multiple OrbitOne inventories, renders a `Tabs` bar (one tab
 * per camera, label = inventory name) and the observation gallery for the selected camera. Filter
 * state is keyed by inventory id so each tab keeps its own selection for the life of the modal.
 */
export const OrbitOneImageGalleryModalContent = ({
  cameras,
  galleryContext,
}: OrbitOneImageGalleryModalContentProps) => {
  const { t } = useTranslation('orbitOne');
  const [selectedCameraId, setSelectedCameraId] = useState(() => cameras[0]?.id ?? '');
  // Filters are kept per camera so each tab remembers its own selection for the life of the modal.
  const [filtersByCameraId, setFiltersByCameraId] = useState<
    Record<string, OrbitOneGalleryFilterState>
  >({});

  if (cameras.length === 0) {
    return (
      <FlexAligner
        direction='column'
        style={{
          padding: responsive.spacing['2xlarge'],
          alignItems: 'flex-start',
        }}
      >
        <Typography type='body_medium'>{t('liceCount.imageGallery.noCameras')}</Typography>
      </FlexAligner>
    );
  }

  const selectedCamera = cameras.find(camera => camera.id === selectedCameraId) ?? cameras[0];

  const isWelfareContext = galleryContext === OrbitOneGalleryContext.Welfare;
  const availableFilterClasses = isWelfareContext ? welfareFilterClasses : liceGalleryFilterClasses;
  // Scale loss is offered in the lice gallery but starts deselected, so the gallery lists fish
  // with lice rather than every fish with scale loss.
  const defaultFilterClasses = isWelfareContext ? welfareFilterClasses : liceFilterClasses;

  const selectedFilters = normalizeGalleryFilterState(
    filtersByCameraId[selectedCamera.id] ??
      createDefaultOrbitOneGalleryFilterState(defaultFilterClasses),
    availableFilterClasses
  );

  const handleFiltersChange = (filters: OrbitOneGalleryFilterState) => {
    setFiltersByCameraId(previous => ({
      ...previous,
      [selectedCamera.id]: filters,
    }));
  };

  return (
    <FlexAligner direction='column' style={{ alignItems: 'stretch' }} fullHeight={true}>
      {cameras.length > 1 && (
        <Tabs>
          {cameras.map(camera => (
            <TabBase
              key={camera.id}
              selected={camera.id === selectedCamera.id}
              onClick={() => setSelectedCameraId(camera.id)}
            >
              {camera.name}
            </TabBase>
          ))}
        </Tabs>
      )}
      <OrbitOneImageGalleryPanel
        camera={selectedCamera}
        filters={selectedFilters}
        onFiltersChange={handleFiltersChange}
        availableFilterClasses={availableFilterClasses}
      />
    </FlexAligner>
  );
};
const createDefaultOrbitOneGalleryFilterState = (
  defaultFilterClasses: OrbitOneGalleryFilterClass[]
): OrbitOneGalleryFilterState => ({
  filters: defaultFilterClasses,
  date: DateTime.now().minus({ days: 1 }),
});

const normalizeGalleryFilterState = (
  state: OrbitOneGalleryFilterState,
  availableFilterClasses: OrbitOneGalleryFilterClass[]
): OrbitOneGalleryFilterState => {
  const availableSelectedFilters = state.filters.filter(filterClass =>
    availableFilterClasses.includes(filterClass)
  );

  return {
    ...state,
    filters: availableSelectedFilters,
  };
};
